import React, { useEffect, useRef } from "react";
import { useTickBuffer } from "@/vybe-trader-client";

/**
 * Rolling bid/ask tick pane for the mobile trade ticket (proposal 18).
 *
 * Renders the last ~60s of ticks from the client-side ring buffer
 * (useTickBuffer) as two area lines. A tiny dedicated canvas is used instead
 * of a klinecharts instance: klinecharts is candle/period based and a dual
 * bid+ask line pane at 86px would require a custom indicator plus synthetic
 * bar aggregation — the canvas mirrors the proposal mock exactly.
 *
 * Colours are Tailwind blue-400 / red-400 (the price-text roles) and the
 * border token #262626 — the same values the design system uses.
 */

const BID_COLOR = "#60a5fa"; // tailwind blue-400 (price up / bid)
const ASK_COLOR = "#f87171"; // tailwind red-400 (price down / ask)
const BID_FILL = "rgba(96,165,250,0.09)";
const ASK_FILL = "rgba(248,113,113,0.07)";
const GRID_COLOR = "rgba(38,38,38,0.8)"; // border token #262626

interface TickChartProps {
  symbol: string;
  windowSec?: number;
}

export default function TickChart({ symbol, windowSec = 60 }: TickChartProps) {
  const points = useTickBuffer(symbol, windowSec);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Match backing store to CSS size × devicePixelRatio for crisp lines.
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const W = Math.max(1, Math.round(rect.width * dpr));
    const H = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    ctx.clearRect(0, 0, W, H);

    // Gridlines (quarters), always drawn so the empty pane still reads as a chart.
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;
    for (let g = 1; g < 4; g++) {
      ctx.beginPath();
      ctx.moveTo(0, (H * g) / 4);
      ctx.lineTo(W, (H * g) / 4);
      ctx.stroke();
    }

    if (points.length < 2) return;

    const now = Date.now();
    const minT = now - windowSec * 1000;
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of points) {
      if (p.bid < lo) lo = p.bid;
      if (p.ask > hi) hi = p.ask;
    }
    const pad = (hi - lo) * 0.15 || Math.abs(hi) * 1e-6 || 1e-6;
    lo -= pad;
    hi += pad;

    const x = (t: number) =>
      ((Math.max(t, minT) - minT) / (windowSec * 1000)) * W;
    const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;

    const drawSeries = (
      pick: (p: { bid: number; ask: number }) => number,
      stroke: string,
      fill: string
    ) => {
      ctx.beginPath();
      points.forEach((p, i) => {
        const px = x(p.t);
        const py = y(pick(p));
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      });
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1.5 * dpr;
      ctx.stroke();
      // Area fill down to the baseline.
      ctx.lineTo(x(points[points.length - 1]!.t), H);
      ctx.lineTo(x(points[0]!.t), H);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
    };

    drawSeries((p) => p.ask, ASK_COLOR, ASK_FILL);
    drawSeries((p) => p.bid, BID_COLOR, BID_FILL);
  }, [points, windowSec]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex h-6 items-center justify-between border-b border-border/60 px-3">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Ticks · rolling {windowSec}s
        </span>
        <span className="font-mono text-[10px] tabular-nums">
          <span className="text-blue-400">— bid</span>{" "}
          <span className="ml-2 text-red-400">— ask</span>
        </span>
      </div>
      <canvas ref={canvasRef} className="block h-[86px] w-full" />
    </div>
  );
}
